/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, February 24th 2022, 8:56:34 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import React from "react";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import Stack from "@mui/material/Stack";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";
import { OSFormInputProps, useControlledInput } from "ui-widgets/forms";
import { FormConfiguration, FormField, IRegistrationFormSettings } from "lib/schemas";
import { ObjectKey } from "lib/types";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import Grid from "@mui/material/Grid";
import { useToggleState } from "ui-widgets";
import { COLORSCHEME } from "theme/styles";
import { FormAPIPatchForm } from "form-builder/components/FormAPIPatchForm";
import { FormInputCustomErrorWrapper, InputHelpDialogWrapper } from "ui-widgets";
import { FieldPicker } from "./FieldPicker";

interface RegistrationFormSettingsPopupProps {
  children: (options: { handleOpen: () => void }) => React.ReactNode;
}

export const RegistrationFormSettingsPopup = ({ children }: RegistrationFormSettingsPopupProps) => {
  const { handleClose, handleOpen, open } = useToggleState();
  return (
    <>
      {children({ handleOpen })}
      <FormAPIPatchForm
        popUpIcon={<AppRegistrationIcon sx={{ color: COLORSCHEME.primaryVariant }} />}
        popUpTitle="Registration Form Settings"
        popUpDescription="Provide the Registration Form Settings below."
        formInfoPopUpOpenState={open}
        closeFormInfoPopUpFn={handleClose}
      >
        <RegistrationFormSettingsField schema_field_key="registration_form_settings" />
      </FormAPIPatchForm>
    </>
  );
};

type RegistrationFormSettingsFieldProps = OSFormInputProps<FormConfiguration>;

const RegistrationFormSettingsField = (props: RegistrationFormSettingsFieldProps) => {
  const {
    useFormReturn,
    field: { onChange, value },
  } = useControlledInput<FormConfiguration>(props);

  const registrationFormSettings = value as IRegistrationFormSettings;

  const onChangeRegistrationFormSettings = (settings: IRegistrationFormSettings) => onChange(settings);

  const errors = useFormReturn.formState?.errors;

  React.useEffect(
    () => useFormReturn.setValue(props.schema_field_key, registrationFormSettings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(registrationFormSettings)]
  );

  const onChangeAuthorize = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, authorize: event.target.checked });
  };

  const onChangeFirstNameField = (field: FormField) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, first_name: field?.artifact_tag });
  };

  const onChangeLastNameField = (field: FormField) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, last_name: field?.artifact_tag });
  };

  const onChangeFullNameField = (field: FormField) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, full_name: field?.artifact_tag });
  };

  const onChangeIdentificationField = (field: FormField) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, identification_field: field?.artifact_tag });
  };

  const onBlurProfileGroup = (event: React.FocusEvent<HTMLInputElement>) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, profile_group: event.target.value });
  };

  const onBlurProfileGroupDescription = (event: React.FocusEvent<HTMLInputElement>) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, profile_group_description: event.target.value });
  };

  const onChangeRegistrationEmailField = (field: FormField) => {
    onChangeRegistrationFormSettings({ ...registrationFormSettings, registration_email: field?.artifact_tag });
  };

  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);

  return (
    <Stack spacing={1}>
      <TextField
        label="Registration Profile Group Name"
        defaultValue={registrationFormSettings?.profile_group}
        onBlur={onBlurProfileGroup}
      />
      <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="profile_group" />
      <TextField
        label="Registration Profile Group Description"
        defaultValue={registrationFormSettings?.profile_group_description}
        onBlur={onBlurProfileGroupDescription}
        multiline
        rows={4}
      />
      <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="profile_group_description" />
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox defaultChecked={registrationFormSettings?.authorize ?? true} onChange={onChangeAuthorize} />
          }
          label="User must authorize use of their identification detail and email address for this registration"
        />
      </FormGroup>
      <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="authorize" />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl>
            <InputHelpDialogWrapper
              helpTitle={<>Next step in the workflow</>}
              helpContent={
                <>
                  <p>
                    This is the field that captures the User's unique Identification e.g. Passport Number, ID number,
                    Membership ID etc.
                  </p>
                </>
              }
            >
              <FieldPicker
                handleChooseField={onChangeIdentificationField}
                label="Choose Identification Field"
                fieldIdentifier={registrationFormSettings?.identification_field}
                formId={currentFormConfiguration.id}
              />
            </InputHelpDialogWrapper>
            <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="identification_field" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl>
            <FieldPicker
              handleChooseField={onChangeRegistrationEmailField}
              label="Choose Registration Email Field"
              fieldIdentifier={registrationFormSettings?.registration_email}
              formId={currentFormConfiguration.id}
            />
            <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="registration_email" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl>
            <FieldPicker
              handleChooseField={onChangeFullNameField}
              label="Choose Full Name Field"
              fieldIdentifier={registrationFormSettings?.full_name}
              formId={currentFormConfiguration.id}
            />
            <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="full_name" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl>
            <FieldPicker
              handleChooseField={onChangeFirstNameField}
              label="Choose First Name Field"
              fieldIdentifier={registrationFormSettings?.first_name}
              formId={currentFormConfiguration.id}
            />
            <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="last_name" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl>
            <FieldPicker
              handleChooseField={onChangeLastNameField}
              label="Choose Last Name Field"
              fieldIdentifier={registrationFormSettings?.last_name}
              formId={currentFormConfiguration.id}
            />
            <RegistrationSettingsFieldErrorWrapper errors={errors} json_field_key="last_name" />
          </FormControl>
        </Grid>
      </Grid>
    </Stack>
  );
};

interface RegistrationSettingsFieldErrorWrapperProps {
  errors: any;
  json_field_key: ObjectKey<IRegistrationFormSettings>;
}

const RegistrationSettingsFieldErrorWrapper = ({
  errors,
  json_field_key,
}: RegistrationSettingsFieldErrorWrapperProps) =>
  errors?.registration_form_settings?.[json_field_key] ? (
    <FormInputCustomErrorWrapper>
      <React.Fragment>{errors?.registration_form_settings?.[json_field_key]?.message}</React.Fragment>
    </FormInputCustomErrorWrapper>
  ) : null;
